<template>
  <controller :lockScroll="true" :goToTopButton="false">
    <section class="atividades-v2-embed-page">
      <div v-if="loading || error" :class="['atividades-v2-state', { error }]">
        <span v-if="loading" class="loader" aria-hidden="true"></span>
        <p>{{ message }}</p>
        <button v-if="error" type="button" @click="startHandoff()">Tentar novamente</button>
      </div>
      <iframe
        v-else
        ref="atividadesV2Frame"
        :src="redirectUrl"
        title="Atividades"
        allow="clipboard-read; clipboard-write"
        @load="onIframeLoad"
      ></iframe>
    </section>
  </controller>
</template>

<script>
import AtividadesV2Service from '@/services/atividades-v2.service'

export default {
  name: 'AtividadesV2Embed',
  data () {
    return {
      error: null,
      loading: true,
      message: 'Abrindo Atividades...',
      redirectUrl: null,
      redirectOverride: null,
      embedOrigin: null,
      heartbeatTimer: null,
      reauthenticating: false,
      iframeReady: false,
      lastEmbedHref: null,
      routeSyncedFromEmbed: null
    }
  },
  watch: {
    '$route.fullPath' (fullPath) {
      if (this.routeSyncedFromEmbed === fullPath) {
        this.routeSyncedFromEmbed = null
        return
      }
      this.redirectOverride = null
      const redirect = this.redirectTo()
      if (this.iframeReady && this.redirectUrl && this.embedOrigin) {
        this.navigateIframe(redirect)
        return
      }
      this.startHandoff(redirect)
    }
  },
  mounted () {
    window.__atividadesV2RealtimeReady = false
    window.addEventListener('message', this.onEmbedMessage)
    this.startHandoff()
  },
  beforeUnmount () {
    window.__atividadesV2RealtimeReady = false
    this.stopHeartbeat()
    window.removeEventListener('message', this.onEmbedMessage)
  },
  methods: {
    startHandoff (redirectOverride = null) {
      const sanitizedRedirect = this.redirectFromHref(redirectOverride)
      if (sanitizedRedirect) this.redirectOverride = sanitizedRedirect

      this.stopHeartbeat()
      this.error = null
      this.loading = true
      this.message = 'Abrindo Atividades...'
      this.redirectUrl = null
      this.embedOrigin = null
      this.iframeReady = false
      this.lastEmbedHref = null

      AtividadesV2Service.redirectUrl({ redirectTo: this.redirectTo() }).then(
        redirectUrl => {
          try {
            const parsed = new URL(redirectUrl)
            if (parsed.protocol !== 'https:' && parsed.hostname !== 'localhost') throw new Error('Origem do módulo de Atividades não é segura.')
            this.embedOrigin = parsed.origin
            this.redirectUrl = redirectUrl
            this.loading = false
          } catch (error) {
            this.showHandoffError(error)
          }
        },
        error => this.showHandoffError(error)
      )
    },
    showHandoffError (error) {
      this.error = error
      this.loading = false
      this.message = error.response?.data?.message || error.message || 'Não foi possível abrir Atividades.'
      window.toaster && window.toaster.emit('open', { style: 'warning', message: this.message, floater: true })
    },
    redirectTo () {
      if (this.redirectOverride) return this.redirectOverride
      const configured = this.$route.meta?.atividadesV2RedirectTo
      const base = typeof configured === 'function' ? configured(this.$route) : (configured || '/')
      const params = new URLSearchParams()

      Object.keys(this.$route.query || {}).forEach(key => {
        if (key === 'email' || key === 'ticket' || key === 'embed') return
        const values = Array.isArray(this.$route.query[key]) ? this.$route.query[key] : [this.$route.query[key]]
        values.forEach(value => {
          if (value !== undefined && value !== null && value !== '') params.append(key, value)
        })
      })

      const query = params.toString()
      const path = query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base
      return `${path}${this.$route.hash || ''}`
    },
    onIframeLoad () {
      this.startHeartbeat()
      this.sendIframeMessage('atividades-v2:keepalive')
    },
    startHeartbeat () {
      this.stopHeartbeat()
      this.heartbeatTimer = window.setInterval(() => this.sendIframeMessage('atividades-v2:keepalive'), 5 * 60 * 1000)
    },
    stopHeartbeat () {
      if (this.heartbeatTimer) window.clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    },
    sendIframeMessage (type, payload = {}) {
      if (!this.embedOrigin) return
      this.$refs.atividadesV2Frame?.contentWindow?.postMessage({ type, ...payload }, this.embedOrigin)
    },
    onEmbedMessage (event) {
      if (event.source !== this.$refs.atividadesV2Frame?.contentWindow || event.origin !== this.embedOrigin) return
      const payload = event.data || {}
      if (payload.source !== 'atividades-v2') return

      if ((payload.type === 'atividades-v2:ready' || payload.type === 'atividades-v2:navigation') && payload.href) {
        const redirect = this.redirectFromHref(payload.href)
        if (redirect) {
          this.iframeReady = true
          this.lastEmbedHref = redirect
          this.redirectOverride = redirect
          this.syncApolloRoute(redirect, payload.type === 'atividades-v2:navigation' ? payload.historyAction : 'replace')
        }
      }
      if (payload.type === 'atividades-v2:realtime-state') {
        window.__atividadesV2RealtimeReady = payload.ready === true
      }
      if (payload.type === 'atividades-v2:session-expired') this.reauthenticate(payload.href)
    },
    navigateIframe (href) {
      const redirect = this.redirectFromHref(href)
      if (!redirect || redirect === this.lastEmbedHref) return
      this.iframeReady = false
      this.redirectOverride = redirect
      this.sendIframeMessage('atividades-v2:navigate', { href: redirect })
    },
    syncApolloRoute (redirect, historyAction = 'replace') {
      const apolloHref = this.apolloHrefFromRedirect(redirect)
      if (!apolloHref || apolloHref === this.$route.fullPath) return
      this.routeSyncedFromEmbed = apolloHref
      const navigate = historyAction === 'push' ? 'push' : 'replace'
      this.$router[navigate](apolloHref).catch(error => {
        this.routeSyncedFromEmbed = null
        if (error?.name !== 'NavigationDuplicated') console.error('Falha ao sincronizar rota do Atividades V2.', error)
      })
    },
    apolloHrefFromRedirect (redirect) {
      const url = new URL(redirect, this.embedOrigin || window.location.origin)
      const params = new URLSearchParams(url.search)
      const withQuery = path => {
        const query = params.toString()
        return `${path}${query ? `?${query}` : ''}${url.hash}`
      }
      const section = ['atendimentos', 'tarefas'].includes(params.get('type')) ? params.get('type') : 'atendimentos'
      const detail = url.pathname.match(/^\/activities\/(\d+)$/)

      if (url.pathname === '/') {
        params.delete('type')
        return withQuery(`/atividades/painel/${section}`)
      }
      if (url.pathname === '/activities/novo') {
        params.delete('type')
        return withQuery(`/atividades/painel/${section}/nova`)
      }
      if (detail) {
        const action = params.get('action') === 'executar' ? 'executar' : 'ver'
        params.delete('type')
        params.delete('action')
        return withQuery(`/atividades/painel/${section}/${action}/${detail[1]}`)
      }
      if (url.pathname === '/activities/importacoes') return withQuery('/atividades/importacoes')
      if (url.pathname === '/activities/pesquisa-avancada') return withQuery('/atividades/pesquisa')
      if (url.pathname === '/config') return withQuery('/atividades/configuracoes')
      if (url.pathname === '/config/aprovacoes') return withQuery('/config/aprovacoes')
      if (url.pathname === '/processos') return withQuery('/atividades/macroprocessos')
      const processView = url.pathname.match(/^\/processos\/(\d+)$/)
      if (processView) return withQuery(`/atividades/macroprocessos/ver/${processView[1]}`)
      const processConfig = url.pathname.match(/^\/config\/processos\/(novo|\d+)$/)
      if (processConfig) {
        return withQuery(processConfig[1] === 'novo'
          ? '/atividades/macroprocessos/novo'
          : `/atividades/macroprocessos/editar/${processConfig[1]}`)
      }
      const responseView = url.pathname.match(/^\/config\/formularios\/respostas\/(\d+)$/)
      if (responseView) return withQuery(`/atividades/respostas/${responseView[1]}`)
      if (url.pathname === '/config/formularios/respostas') return withQuery('/atividades/respostas')
      const formView = url.pathname.match(/^\/config\/formularios\/view\/(\d+)$/)
      if (formView) return withQuery(`/atividades/formularios/view/${formView[1]}`)
      if (url.pathname === '/config/categorias') {
        const categoryPath = params.get('type') === 'atendimentos' ? '/atividades/atendimentos/categorias' : '/atividades/categorias'
        if (params.get('type') === 'atendimentos') params.delete('type')
        return withQuery(categoryPath)
      }

      const configRoutes = [
        { matcher: /^\/config\/categorias\/(novo|\d+)$/, base: '/atividades/categorias' },
        { matcher: /^\/config\/formularios(?:\/(novo|\d+))?$/, base: '/atividades/formularios' },
        { matcher: /^\/config\/gatilhos(?:\/(novo|\d+))?$/, base: '/atividades/gatilhos' },
        { matcher: /^\/config\/bases(?:\/(novo|\d+))?$/, base: '/atividades/bases' },
        { matcher: /^\/config\/fluxos(?:\/(novo|\d+))?$/, base: '/atividades/fluxos' }
      ]
      for (const configRoute of configRoutes) {
        const match = url.pathname.match(configRoute.matcher)
        if (!match) continue
        if (!match[1]) return withQuery(configRoute.base)
        if (match[1] === 'novo') return withQuery(`${configRoute.base}/novo`)
        return withQuery(`${configRoute.base}/editar/${match[1]}`)
      }

      return withQuery(`/atividades/modulo${url.pathname === '/' ? '' : url.pathname}`)
    },
    reauthenticate (href) {
      if (this.reauthenticating) return
      this.reauthenticating = true
      this.startHandoff(href)
      window.setTimeout(() => { this.reauthenticating = false }, 1500)
    },
    redirectFromHref (href) {
      if (!href || typeof href !== 'string') return null
      try {
        const url = new URL(href, this.embedOrigin || window.location.origin)
        if (this.embedOrigin && url.origin !== this.embedOrigin) return null
        const redirect = `${url.pathname}${url.search}${url.hash}`
        return redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null
      } catch {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.atividades-v2-embed-page {
  background-color: $color-bg;
  display: flex;
  inset: 0;
  min-height: 0;
  overflow: hidden;
  position: absolute;
  width: 100%;

  iframe { border: 0; display: block; flex: 1; height: 100%; min-height: 0; width: 100%; }
}

.atividades-v2-state {
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  font-weight: bold;
  gap: $hmg_small;
  justify-content: center;
  padding: $mg;
  text-align: center;

  .loader {
    animation: atividades-loading-spin .8s linear infinite;
    border: 3px solid rgba($color-primary, .16);
    border-radius: 50%;
    border-top-color: $color-primary;
    display: block;
    height: 30px;
    width: 30px;
  }

  button { background: $color-primary; border: 0; border-radius: 24px; color: $color-secondary; cursor: pointer; min-height: 36px; padding: 0 $hmg_mid; }
  &.error { color: $color-primary; }
}

@keyframes atividades-loading-spin {
  to { transform: rotate(360deg); }
}
</style>
